/**
 * One project: the collapsed lane, and the phase rows underneath when it is opened.
 *
 * The collapsed row is the product. The workbook drew six task rows per project -
 * fifty-four rows for nine projects, which is more than fits on a screen and far
 * more than fits in a head - and the whole point of compressing each group to a
 * single row is that the single row still answers "where has this got to". It does
 * that with one composite bar spanning the project's earliest start to its latest
 * end, coloured by the state that utils/phaseState.ts picks out.
 *
 * Phases stay collapsed by default. Expanding is for when the summary raises a
 * question, not for reading the roadmap.
 */

import { useState } from 'react';
import styled from 'styled-components';

import { palette, radius, STATE_STYLE } from '../../styles/theme';
import { Chip } from '../../styles/ui';
import type { Grid } from '../../utils/dates';
import { describeVerdict, laneVerdict, phaseState } from '../../utils/phaseState';
import type { Person, Phase, Project, ProjectPatch } from '../../types';
import PhaseEditor from '../PhaseEditor';
import ProjectEditor from '../ProjectEditor';
import Bar, { describeProgress, describeSpan } from './Bar';
import {
  Disclosure,
  EditButton,
  EditorRow,
  LaneLabelCell,
  LaneName,
  LaneRow,
  PhaseLabelCell,
  PhaseName,
  PhaseRow,
  SubLabel,
  Track,
  UnscheduledStrip,
} from './parts';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

/** The state's colour as a dot, so the lane name carries the state too. */
const StateDot = styled.span<{ $fill: string }>`
  flex: none;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => p.$fill};
  border: 1px solid rgba(46, 21, 36, 0.15);
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
`;

/** A phase with no dates: small and stated, not a full-width banner. */
const NoDates = styled.span`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 600;
  color: ${palette.slateDeep};
  border: 1px dashed ${palette.borderStrong};
  border-radius: ${radius.pill};
  padding: 1px 9px;
  white-space: nowrap;
`;

const SupportChip = styled(Chip)`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  color: ${palette.slateDeep};
  border-color: ${palette.border};
  background: rgba(180, 162, 172, 0.12);
`;

export interface LaneProps {
  project: Project;
  grid: Grid;
  people: Person[];
  today: string;
  expanded: boolean;
  onToggle: () => void;
  onPhaseSaved: (phase: Phase) => void;
  onProjectSaved: (projectId: string, patch: ProjectPatch) => void;
}

export default function Lane({
  project,
  grid,
  people,
  today,
  expanded,
  onToggle,
  onPhaseSaved,
  onProjectSaved,
}: LaneProps) {
  const [editingProject, setEditingProject] = useState(false);
  const [editingPhaseId, setEditingPhaseId] = useState<string | null>(null);

  const verdict = laneVerdict(project.phases, today);
  const style = STATE_STYLE[verdict.state];
  const nameOf = (email: string | null) =>
    email ? (people.find((p) => p.email === email)?.name ?? email) : null;

  const dri = nameOf(project.dri_email);
  const support = nameOf(project.support_email);
  // "DRI unassigned" is printed rather than left blank. Four of the nine lanes have
  // no DRI and the workbook showed that as an empty cell, which reads as "nothing to
  // see" instead of "nobody owns this".
  const owners = dri
    ? `DRI ${dri}${support ? ` · Support ${support}` : ''}`
    : 'DRI unassigned';

  const laneTitle = [
    project.name,
    describeVerdict(verdict),
    describeSpan(verdict.start, verdict.end),
    verdict.progress === null
      ? 'no progress recorded on any phase'
      : `average progress ${describeProgress(verdict.progress)} across ${
          verdict.realCount - verdict.progressUnknownCount
        } of ${verdict.realCount} phases`,
  ].join(' — ');

  const laneCaption =
    verdict.basis === 'complete'
      ? 'Complete'
      : `${style.label} · ${describeProgress(verdict.progress)}`;

  return (
    <>
      <LaneRow>
        <LaneLabelCell>
          <Disclosure
            type="button"
            $open={expanded}
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${project.name}`}
          >
            {/* A triangle glyph rather than an icon dependency; parts.ts rotates it. */}
            &#9656;
          </Disclosure>
          <Stack>
            <NameRow>
              <StateDot $fill={style.fill} aria-hidden />
              <LaneName title={project.name}>{project.name}</LaneName>
            </NameRow>
            <SubLabel title={owners}>
              {describeVerdict(verdict)} · {owners}
            </SubLabel>
          </Stack>
          <EditButton type="button" onClick={() => setEditingProject((open) => !open)}>
            Edit
          </EditButton>
        </LaneLabelCell>

        <Track>
          {verdict.start || verdict.end ? (
            <Bar
              grid={grid}
              start={verdict.start}
              end={verdict.end}
              state={verdict.state}
              progress={verdict.progress}
              caption={laneCaption}
              title={laneTitle}
            />
          ) : (
            <UnscheduledStrip title={laneTitle}>
              {verdict.basis === 'empty' ? 'No phases yet' : 'Not scheduled'}
            </UnscheduledStrip>
          )}
        </Track>
      </LaneRow>

      {editingProject ? (
        <EditorRow>
          <ProjectEditor
            project={project}
            people={people}
            onCancel={() => setEditingProject(false)}
            onSaved={(patch) => {
              onProjectSaved(project.project_id, patch);
              setEditingProject(false);
            }}
          />
        </EditorRow>
      ) : null}

      {expanded
        ? project.phases.map((phase) => {
            const state = phaseState(phase.name);
            const owner = nameOf(phase.owner_email);
            const title = `${project.name} — ${phase.name}: ${describeSpan(
              phase.start,
              phase.end
            )}, ${phase.structural ? 'ongoing support' : describeProgress(phase.progress)}`;

            return (
              <div key={phase.phase_id}>
                <PhaseRow>
                  <PhaseLabelCell>
                    <PhaseName title={phase.name}>{phase.name}</PhaseName>
                    <SubLabel>{owner ?? 'unassigned'}</SubLabel>
                    <EditButton
                      type="button"
                      onClick={() =>
                        setEditingPhaseId((current) =>
                          current === phase.phase_id ? null : phase.phase_id
                        )
                      }
                    >
                      Edit
                    </EditButton>
                  </PhaseLabelCell>
                  <Track>
                    {phase.structural ? (
                      // Maintenance. It has no dates by construction and is not a
                      // gap, so it gets a label rather than an empty row that looks
                      // like missing data.
                      <SupportChip title={title}>Ongoing support</SupportChip>
                    ) : phase.start || phase.end ? (
                      <Bar
                        grid={grid}
                        start={phase.start}
                        end={phase.end}
                        state={state}
                        progress={phase.progress}
                        caption={describeProgress(phase.progress)}
                        title={title}
                      />
                    ) : (
                      <NoDates title={title}>No dates set</NoDates>
                    )}
                  </Track>
                </PhaseRow>

                {editingPhaseId === phase.phase_id ? (
                  <EditorRow>
                    <PhaseEditor
                      phase={phase}
                      people={people}
                      onCancel={() => setEditingPhaseId(null)}
                      onSaved={(saved) => {
                        onPhaseSaved(saved);
                        setEditingPhaseId(null);
                      }}
                    />
                  </EditorRow>
                ) : null}
              </div>
            );
          })
        : null}
    </>
  );
}
